import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Home, Briefcase, MapPin, Plus, Check, ChevronRight, X, Loader2, Pencil, Save } from 'lucide-react';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';

const ICON_MAP = {
  casa: Home,
  hogar: Home,
  trabajo: Briefcase,
  oficina: Briefcase,
};

function getIcon(label) {
  const lower = (label || '').toLowerCase();
  for (const [key, Icon] of Object.entries(ICON_MAP)) {
    if (lower.includes(key)) return Icon;
  }
  return MapPin;
}

export default function AddressSelectorSheet({ open, onClose, onNewLocation, onSelect }) {
  const { user } = useAuth();
  const { zone } = useCart();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(null);
  const [editingAddr, setEditingAddr] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', address_text: '' });

  useEffect(() => {
    if (!open) { setEditingAddr(null); return; }
    setLoading(true);
    api.get('/direcciones').then(r => {
      setAddresses(r.data.direcciones || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [open]);

  const startEdit = (e, addr) => {
    e.stopPropagation();
    setEditingAddr(addr.id);
    setEditForm({ label: addr.label || '', address_text: addr.address_text || '' });
  };

  const saveEdit = async (e) => {
    e.stopPropagation();
    try {
      const { data } = await api.put(`/direcciones/${editingAddr}`, editForm);
      setAddresses(prev => prev.map(a => a.id === editingAddr ? { ...a, ...data.direccion } : a));
      setEditingAddr(null);
    } catch { /* ignore */ }
  };

  // Sort: last_address_id first, then by label
  const sorted = useMemo(() => {
    if (!addresses.length) return [];
    const lastId = user?.last_address_id;
    return [...addresses].sort((a, b) => {
      if (a.id === lastId) return -1;
      if (b.id === lastId) return 1;
      return 0;
    });
  }, [addresses, user?.last_address_id]);

  const isLast = (addr) => addr.id === user?.last_address_id;

  const handleSelect = async (addr) => {
    if (!addr.lat || !addr.lng) {
      // Address without coordinates, go to map
      onNewLocation();
      return;
    }
    // Validate zone for this address
    setValidating(addr.id);
    try {
      const { data } = await api.post('/zonas-entrega/validar-ubicacion', { lat: addr.lat, lng: addr.lng });
      if (data.dentro_de_zona) {
        // Update last_address_id silently
        api.put('/auth/perfil', { last_address_id: addr.id }).catch(() => {});
        onSelect(addr, data.zona);
      } else {
        // Out of zone - still let them pick, but warn
        onSelect(addr, null);
      }
    } catch {
      onSelect(addr, null);
    } finally {
      setValidating(null);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[9998] flex items-end justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        data-testid="address-selector-sheet"
        className="w-full max-w-lg bg-zinc-950 rounded-t-3xl shadow-[0_-8px_32px_rgba(0,0,0,0.5)] animate-[slideUp_0.3s_ease-out]"
        onClick={e => e.stopPropagation()}
        style={{ maxHeight: '85vh', overflow: 'auto' }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-zinc-700" />
        </div>

        <div className="px-5 pb-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold">¿Dónde querés recibir tu pedido?</h2>
              <p className="text-xs text-zinc-500 mt-0.5">Seleccioná una dirección guardada</p>
            </div>
            <button onClick={onClose} className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700">
              <X size={16} className="text-zinc-400" />
            </button>
          </div>

          {/* Addresses */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 size={24} className="animate-spin text-zinc-500" />
            </div>
          ) : (
            <div className="space-y-2">
              {sorted.map(addr => {
                const Icon = getIcon(addr.label);
                const isSelected = isLast(addr);
                const isValidating = validating === addr.id;
                const isEditing = editingAddr === addr.id;

                if (isEditing) {
                  return (
                    <div key={addr.id} className="p-4 rounded-2xl border border-[#E84C1C]/30 bg-zinc-900/80 space-y-2" onClick={e => e.stopPropagation()}>
                      <input
                        data-testid={`addr-edit-label-${addr.id}`}
                        value={editForm.label}
                        onChange={e => setEditForm(f => ({ ...f, label: e.target.value }))}
                        placeholder="Etiqueta (Casa, Trabajo...)"
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm placeholder:text-zinc-600"
                      />
                      <input
                        data-testid={`addr-edit-text-${addr.id}`}
                        value={editForm.address_text}
                        onChange={e => setEditForm(f => ({ ...f, address_text: e.target.value }))}
                        placeholder="Dirección escrita (calle, nro, depto)"
                        className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm placeholder:text-zinc-600"
                      />
                      <div className="flex gap-2">
                        <button onClick={saveEdit} data-testid={`addr-edit-save-${addr.id}`} className="flex-1 flex items-center justify-center gap-1 bg-[#E84C1C] text-white text-xs py-2 rounded-lg"><Save size={12} /> Guardar</button>
                        <button onClick={() => setEditingAddr(null)} className="flex-1 text-xs text-zinc-400 py-2 rounded-lg border border-zinc-800 hover:bg-zinc-800">Cancelar</button>
                      </div>
                    </div>
                  );
                }

                return (
                  <button
                    key={addr.id}
                    data-testid={`addr-select-${addr.id}`}
                    onClick={() => handleSelect(addr)}
                    disabled={isValidating}
                    className={`w-full text-left flex items-center gap-3.5 p-4 rounded-2xl border transition-all active:scale-[0.98] ${
                      isSelected
                        ? 'bg-[#E84C1C]/10 border-[#E84C1C]/30 shadow-[0_0_12px_rgba(232,76,28,0.1)]'
                        : 'bg-zinc-900/60 border-zinc-800/50 hover:border-zinc-700 hover:bg-zinc-800/40'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${isSelected ? 'bg-[#E84C1C]/20' : 'bg-zinc-800'}`}>
                      <Icon size={22} className={isSelected ? 'text-[#E84C1C]' : 'text-zinc-400'} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{addr.label || 'Mi dirección'}</span>
                        {isSelected && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#E84C1C]/20 text-[#E84C1C] font-medium">Última</span>}
                      </div>
                      <p className="text-xs text-zinc-400 mt-0.5 truncate">{addr.address_text || addr.address_line}</p>
                    </div>
                    <div className="shrink-0 flex items-center gap-1">
                      <button onClick={(e) => startEdit(e, addr)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-zinc-700/50 text-zinc-500 hover:text-zinc-300 transition-colors" data-testid={`addr-edit-btn-${addr.id}`}>
                        <Pencil size={13} />
                      </button>
                      {isValidating ? (
                        <Loader2 size={18} className="animate-spin text-[#E84C1C]" />
                      ) : isSelected ? (
                        <Check size={18} className="text-[#E84C1C]" />
                      ) : (
                        <ChevronRight size={18} className="text-zinc-600" />
                      )}
                    </div>
                  </button>
                );
              })}

              {addresses.length === 0 && (
                <div className="text-center py-6">
                  <MapPin size={28} className="mx-auto text-zinc-600 mb-2" />
                  <p className="text-sm text-zinc-400">No tenés direcciones guardadas</p>
                  <p className="text-xs text-zinc-600 mt-1">Marcá una ubicación en el mapa</p>
                </div>
              )}
            </div>
          )}

          {/* New location button */}
          <button
            data-testid="addr-new-location-btn"
            onClick={onNewLocation}
            className="w-full flex items-center gap-3 mt-3 p-3.5 rounded-2xl border border-dashed border-zinc-700 text-zinc-400 hover:text-white hover:border-zinc-500 transition-colors"
          >
            <div className="w-10 h-10 rounded-xl bg-zinc-800 flex items-center justify-center">
              <Plus size={18} />
            </div>
            <span className="text-sm font-medium">Nueva ubicación</span>
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
